import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/Auth/AuthContext';
import { getApiUrl } from '../config/api';

export type ThumbnailMethod = 'ai_image' | 'upload' | 'extract' | 'existing_caption' | 'existing' | 'library';

interface VideoUrls {
  original?: string;
  generated?: string;
  captioned?: string;
}

interface ThumbnailModalState {
  isOpen: boolean;
  selectedMethod: ThumbnailMethod;
  description: string;
  isGenerating: boolean;
  availableMethods: ThumbnailMethod[];
  bestVideoUrl?: string;
}

interface ThumbnailModalHandlers {
  openModal: () => void;
  closeModal: () => void;
  setMethod: (method: ThumbnailMethod) => void;
  setDescription: (description: string) => void;
  handleGeneration: (advancedParams?: any) => Promise<void>;
  handleImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleVideoFrameSelection: () => void;
  handleExistingThumbnail: () => void;
}

export const useThumbnailModal = (
  projectId: string, 
  projectTitle?: string,
  customVideoFrameHandler?: () => void,
  existingThumbnailUrl?: string,
  videoUrls?: VideoUrls
) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // Helper function to determine current app context for cancel navigation
  const getCurrentAppName = (): string => {
    const currentPath = window.location.pathname;
    if (currentPath.includes('/script')) return 'script-generator';
    if (currentPath.includes('/prompt')) return 'prompt-generator';
    if (currentPath.includes('/audio')) return 'audio-editor';
    if (currentPath.includes('/title')) return 'title-description';
    if (currentPath.includes('/thumbnail')) return 'thumbnail-landing';
    if (currentPath.includes('/caption')) return 'caption';
    if (currentPath.includes('/create-content')) return 'create-content';
    if (currentPath.includes('/sound')) return 'sound-editor';
    if (currentPath.includes('/social-media')) return 'social-media';
    if (currentPath.includes('/video-editor')) return 'video-editor';
    
    // Fallback: try to extract the base app name from the URL pattern
    const pathSegments = currentPath.split('/').filter(segment => segment.length > 0);
    if (pathSegments.length > 0) {
      const baseApp = pathSegments[0];
      // Return the first segment as the app name (e.g., 'prompt-generator', 'audio-editor')
      return baseApp;
    }
    
    return 'thumbnail-landing'; // Safe fallback that exists
  };

  // Helper function for FormData requests (no Content-Type header) - used by upload
  const getAuthHeadersForFormData = (): Record<string, string> => {
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Determine best video URL (priority: captioned > generated > original)
  const getBestVideoUrl = (): string | undefined => {
    if (videoUrls?.captioned && videoUrls.captioned.trim()) return videoUrls.captioned;
    if (videoUrls?.generated && videoUrls.generated.trim()) return videoUrls.generated;
    if (videoUrls?.original && videoUrls.original.trim()) return videoUrls.original;
    return undefined;
  };

  // Determine available methods based on context
  const getAvailableMethods = (): ThumbnailMethod[] => {
    const methods: ThumbnailMethod[] = [];
    
    // Always available
    methods.push('ai_image', 'upload', 'library');
    
    // Add existing thumbnail option if available and not empty
    if (existingThumbnailUrl && existingThumbnailUrl.trim()) {
      methods.unshift('existing'); // Add at beginning as preferred option
    }
    
    // Add video-based options if video available and not empty
    const hasVideo = getBestVideoUrl();
    if (hasVideo) {
      methods.push('extract', 'existing_caption');
    }
    
    // console.log('🎯 [MODAL] Available methods:', methods);
    // console.log('🎯 [MODAL] Best video URL:', hasVideo);
    // console.log('🎯 [MODAL] Existing thumbnail:', existingThumbnailUrl);
    
    return methods;
  };

  const availableMethods = getAvailableMethods();
  const bestVideoUrl = getBestVideoUrl();

  // Modal state
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<ThumbnailMethod>(
    availableMethods[0] || 'ai_image' // Default to first available method
  );
  const [description, setDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Handlers
  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setIsOpen(false);
    // Reset state when closing to prevent stale data
    setSelectedMethod(availableMethods[0] || 'ai_image');
    setDescription('');
    setIsGenerating(false);
  };
  const setMethod = (method: ThumbnailMethod) => setSelectedMethod(method);
  const setDescriptionText = (desc: string) => setDescription(desc);

  const handleExistingThumbnail = () => {
    console.log('✅ [EXISTING] Using existing thumbnail');
    showNotification({
      title: 'Success',
      message: 'Continuing with existing thumbnail!',
      type: 'success'
    });
    
    // Close modal and navigate to thumbnail editor to view/edit existing
    closeModal();
    navigate(`/thumbnail-editor/${projectId}`);
  };

  const handleGeneration = async (advancedParams?: any) => {
    console.log('🎯 [GENERATE] handleThumbnailGeneration called with method:', selectedMethod);
    
    if (!projectId) {
      console.log('❌ [GENERATE] No projectId, returning');
      return;
    }

    // Don't handle extract method here - it should be handled by the video frame modal
    if (selectedMethod === 'extract') {
      console.log('⚠️ [GENERATE] Extract method called in handleThumbnailGeneration - this should not happen');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      let prompt = '';
      
      if (selectedMethod === 'ai_image') {
        // AI Image method - use project title as default prompt, user can modify in AI Generator
        prompt = 'Create an engaging thumbnail ...';
      } else if (selectedMethod === 'existing_caption') {
        // Use project title as prompt for existing caption
        prompt = projectTitle || 'Generate an engaging thumbnail';
      }

      console.log('🎨 [GENERATE] Navigating to AI Generator with prompt:', prompt);

      // Navigate to Thumbnail Landing (which has AI Generator) with URL parameters
      const params = new URLSearchParams({
        'project-id': projectId,
        'initial-prompt': prompt,
        'next_path': '/thumbnail-landing', // Force AI Generator even with characters
        'return-to': 'thumbnail-editor',
        'cancel-to': getCurrentAppName(), // Dynamic cancel destination
        'mode': 'thumbnail',
        'aspect-ratio': '16:9',
        'max-images': '4',
        'provider': advancedParams?.selectedProvider || 'runway'
      });

      // Preserve existing advanced parameters for characters, reference images, etc.
      if (advancedParams?.advanced_image) {
        if (advancedParams.advanced_image.reference_images?.length > 0) {
          params.append('has-reference-images', 'true');
          // Note: Actual reference images will be handled by existing AI Generator logic
        }
        if (advancedParams.advanced_image.style_preset) {
          params.append('style-preset', advancedParams.advanced_image.style_preset);
        }
        if (advancedParams.advanced_image.lighting_style) {
          params.append('lighting-style', advancedParams.advanced_image.lighting_style);
        }
        if (advancedParams.advanced_image.quality_preset) {
          params.append('quality-preset', advancedParams.advanced_image.quality_preset);
        }
        if (advancedParams.advanced_image.enhance_details !== undefined) {
          params.append('enhance-details', advancedParams.advanced_image.enhance_details.toString());
        }
        if (advancedParams.advanced_image.color_boost !== undefined) {
          params.append('color-boost', advancedParams.advanced_image.color_boost.toString());
        }
        if (advancedParams.advanced_image.improve_composition !== undefined) {
          params.append('improve-composition', advancedParams.advanced_image.improve_composition.toString());
        }
      }

      // Close modal and navigate to Thumbnail Landing (which has working AI Generator)
      closeModal();
      navigate(`/thumbnail-landing?${params.toString()}`);

      showNotification({
        title: 'Opening AI Generator',
        message: 'Redirecting to AI image generation...',
        type: 'success'
      });

    } catch (error) {
      console.error('❌ [GENERATE] Error navigating to AI Generator:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to open AI Generator',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔥 [UPLOAD] handleImageUpload called');
    const file = event.target.files?.[0];
    console.log('📁 [UPLOAD] Selected file:', file);
    console.log('🆔 [UPLOAD] Project ID:', projectId);
    
    if (!file || !projectId) {
      console.log('❌ [UPLOAD] Missing file or projectId');
      return;
    }

    if (!file.type.startsWith('image/')) {
      console.log('❌ [UPLOAD] Invalid file type:', file.type);
      showNotification({
        title: 'Error',
        message: 'Please select a valid image file',
        type: 'error'
      });
      return;
    }

    console.log('⏳ [UPLOAD] Starting upload process...');
    setIsGenerating(true);

    try {
      const formData = new FormData();
      formData.append('method', 'upload');
      formData.append('image', file);
      formData.append('projectId', projectId);
      formData.append('title', `${projectTitle || 'Project'} - Uploaded Thumbnail`);
      formData.append('width', '1536');
      formData.append('height', '864');

      console.log('📤 [UPLOAD] Sending request to:', getApiUrl('/thumbnail/generate'));
      console.log('📤 [UPLOAD] FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
      }

      const response = await fetch(getApiUrl('/thumbnail/generate'), {
        method: 'POST',
        headers: getAuthHeadersForFormData(),
        body: formData
      });

      console.log('📬 [UPLOAD] Response status:', response.status);
      console.log('📬 [UPLOAD] Response ok:', response.ok);

      if (!response.ok) {
        console.log('❌ [UPLOAD] Response not ok, trying to get error details...');
        try {
          const errorData = await response.json();
          console.log('❌ [UPLOAD] Error data:', errorData);
          throw new Error(errorData.message || 'Failed to upload thumbnail');
        } catch (jsonError) {
          console.log('❌ [UPLOAD] Could not parse error JSON:', jsonError);
          throw new Error(`Upload failed with status ${response.status}`);
        }
      }

      const data = await response.json();
      console.log('✅ [UPLOAD] Thumbnail upload successful:', data);

      showNotification({
        title: 'Success',
        message: 'Thumbnail uploaded successfully!',
        type: 'success'
      });

      // Close modal and navigate to thumbnail editor to see result
      closeModal();
      navigate(`/thumbnail-editor/${projectId}`);

    } catch (error) {
      console.error('❌ [UPLOAD] Error uploading thumbnail:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to upload thumbnail',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleVideoFrameSelection = () => {
    if (customVideoFrameHandler) {
      // Use custom handler if provided (for components with video frame modal)
      customVideoFrameHandler();
    } else {
      // Default behavior: close modal and navigate to thumbnail editor
      closeModal();
      navigate(`/thumbnail-editor/${projectId}`);
    }
  };

  const modalState: ThumbnailModalState = {
    isOpen,
    selectedMethod,
    description,
    isGenerating,
    availableMethods,
    bestVideoUrl
  };

  const handlers: ThumbnailModalHandlers = {
    openModal,
    closeModal,
    setMethod,
    setDescription: setDescriptionText,
    handleGeneration,
    handleImageUpload,
    handleVideoFrameSelection,
    handleExistingThumbnail
  };

  return {
    modalState,
    handlers
  };
};